import { ChangeEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, IconButton, Typography } from "@mui/material";

import { colors } from "../../../../../theme/palette";
import { BackToGroupIconBtn, WhyCreateBtnIcon } from "../../../../../assets/icons/MUI/GroupsIcons";
import { UserInfo } from "../../../../../types";

import WhyCreateGroupPopUp from "./WhyCreateGroupPopUp";
import GroupDataForm from "./GroupDataForm";
import GroupPreview from "./GroupPreview";
import ConfirmRemoveUserPopUp from "./ConfirmRemoveUserPopUp";
import { useGroupDetails } from "../../../../../api/queries/groups/useGroupsQueries";
import APP_ROUTES from "../../../../../routers/routeConstants";
import { useImageUploader } from "../../../../../shared/ImageUploader/useImageUploader";

const GroupDetails = () => {
  const [isWhyCreateGroupPopUpOpen, setIsWhyCreateGroupPopUpOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const navigate = useNavigate();

  const { groupDetails, isGroupDetailsLoading, isGroupDetailsHasError } = useGroupDetails();

  const [formData, setFormData] = useState({
    groupMedia: groupDetails?.imageLink || "",
    groupName: groupDetails?.name || "",
    groupDescription: groupDetails?.description || "",
  });
  const [membersList, setMembersList] = useState<UserInfo[]>(groupDetails?.users || []);

  useEffect(() => {
    setFormData({
      groupMedia: groupDetails?.imageLink || "",
      groupName: groupDetails?.name || "",
      groupDescription: groupDetails?.description || "",
    });
    setMembersList(groupDetails?.users || []);
  }, [groupDetails]);

  const [errors, setErrors] = useState({
    groupMedia: "",
    groupName: "",
    groupDescription: "",
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const [isRemovePopUpOpened, setIsRemovePopUpOpened] = useState<boolean>(false);
  const [removedUser, setRemovedUser] = useState<UserInfo | null>(null);

  const handleCloseRemovePopUp = () => {
    setIsRemovePopUpOpened(false);
    setRemovedUser(null);
  };

  const handleConfirmRemoveUser = () => {
    setMembersList((prevList) => prevList.filter((member: UserInfo) => member.id !== removedUser?.id));
    handleCloseRemovePopUp();
  };

  const handleRemoveUser = (user: UserInfo | null) => {
    if (!user) return;
    setIsRemovePopUpOpened(true);
    setRemovedUser(user);
  };

  const { IMGName, handleUploadToS3, isImageUploading, previewIMG, handleUploadStart, handleUploadComplete } =
    useImageUploader();

  return (
    <Box
      sx={{
        display: "flex",
        columnGap: "15px",
        width: "855",
      }}
    >
      <Box
        sx={{
          padding: "12px",
          background: colors.White,
          borderRadius: "8px",
          border: "1px solid #EAECF0",
          width: "555px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            columnGap: "14px",
            marginBottom: "16px",
            alignItems: "center",
          }}
        >
          <IconButton
            onClick={() => navigate(APP_ROUTES.ENTITY)}
            sx={{
              transform: currentLang === "en" ? "unset" : "rotate(180deg)",
            }}
          >
            <BackToGroupIconBtn />
          </IconButton>
          <Typography variant="h4ExtraBold" sx={{ color: colors.Black100 }}>
            {t("pages.entity.groups.groupDetails")}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            marginBottom: "32px",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px",
            borderRadius: "8px",
            background: colors.Green25,
          }}
        >
          <Typography variant="body12SemiBold">
            <Box component="span" sx={{ display: "inline-block", marginRight: "8px" }}>
              •
            </Box>
            {t("pages.entity.groups.createGroupEasy")}
          </Typography>
          <Button variant="text-sm" endIcon={<WhyCreateBtnIcon />} onClick={() => setIsWhyCreateGroupPopUpOpen(true)}>
            {t("pages.entity.groups.whyCreateGroup")}
          </Button>
        </Box>
        <WhyCreateGroupPopUp
          isWhyCreateGroupPopUpOpen={isWhyCreateGroupPopUpOpen}
          setIsWhyCreateGroupPopUpOpen={setIsWhyCreateGroupPopUpOpen}
        />
        <Typography
          variant="body16Bold"
          sx={{
            display: "block",
            color: colors.Dark1000,
            marginBottom: "14px",
            textAlign: "center",
          }}
        >
          {t("pages.entity.groups.defineGroup")}
        </Typography>
        <GroupDataForm
          isDataLoading={isGroupDetailsLoading}
          formData={formData}
          setFormData={setFormData}
          handleChange={handleChange}
          errors={errors}
          membersList={membersList}
          setMembersList={setMembersList}
          handleRemoveUser={handleRemoveUser}
          isImageUploading={isImageUploading}
          handleUploadStart={handleUploadStart}
          handleUploadComplete={handleUploadComplete}
        />
      </Box>
      <GroupPreview
        isDataLoading={isGroupDetailsLoading}
        data={formData}
        membersList={membersList}
        setMembersList={setMembersList}
        handleRemoveUser={handleRemoveUser}
        handleUploadImg={handleUploadToS3}
        imagePrev={previewIMG}
        imageName={IMGName}
      />
      <ConfirmRemoveUserPopUp
        open={isRemovePopUpOpened}
        handleClose={handleCloseRemovePopUp}
        handleRemove={handleConfirmRemoveUser}
        userName={removedUser?.firstName || ""}
      />
    </Box>
  );
};

export default GroupDetails;
